// pages/ContactUsPage.tsx - Simple Google Forms Integration
import React, { useState, FormEvent, useRef } from 'react';
import { serviceOptions, mapEmbedUrl } from '../data/constants';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import HoneypotField from '../components/ui/HoneypotField';
import Section from '../components/layout/Section';
import { trackPhoneClick, trackContactFormSubmission, trackBlockedSubmission } from '../utils/tracking';
import { checkSubmission, HONEYPOT_FIELD, SpamCheck } from '../utils/spamGate';
import { featureFlags } from '../config/featureFlags';

const ContactUsPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const renderedAt = useRef<number>(Date.now());

  // Your actual Google Form URL
  const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/1PyWyz7Dk8a9RTmU8zaPMQFbWgMwPpiHXX9sIg5aOFuY/formResponse';

  // Your actual Google Form field names
  const FORM_FIELDS = {
    fullName: 'entry.1113686558',
    email: 'entry.1807368957',
    phone: 'entry.1468226126',
    service: 'entry.787213022',
    message: 'entry.1978730067'
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(event.currentTarget);

    const spam: SpamCheck = featureFlags.spamGateEnabled
      ? checkSubmission({
          honeypot: formData.get(HONEYPOT_FIELD) as string,
          name: formData.get('fullName') as string,
          message: formData.get('message') as string,
          elapsedMs: Date.now() - renderedAt.current,
        })
      : { verdict: 'clean', reasons: [] };

    // Honeypot hit: nothing goes to Google, nothing is reported as a conversion.
    // The bot still sees the normal success state so it has no signal to adapt.
    if (spam.verdict === 'blocked') {
      trackBlockedSubmission('contact', spam);
      setIsSubmitted(true);
      formRef.current?.reset();
      setTimeout(() => setIsSubmitted(false), 5000);
      setIsSubmitting(false);
      return;
    }

    // Create FormData for Google Forms
    const googleFormData = new FormData();
    googleFormData.append(FORM_FIELDS.fullName, formData.get('fullName') as string);
    googleFormData.append(FORM_FIELDS.email, formData.get('email') as string);
    googleFormData.append(FORM_FIELDS.phone, formData.get('phone') as string);
    googleFormData.append(FORM_FIELDS.service, formData.get('service') as string);
    googleFormData.append(FORM_FIELDS.message, formData.get('message') as string);

    try {
      // Submit to Google Forms (using no-cors mode)
      await fetch(GOOGLE_FORM_URL, {
        method: 'POST',
        body: googleFormData,
        mode: 'no-cors' // This bypasses CORS but you won't get a response
      });

      // Track conversion in GA4. Suppressed for flagged submissions so junk
      // never becomes Smart Bidding training data.
      trackContactFormSubmission(formData.get('service') as string, spam);

      // Since we can't get a response due to CORS, assume success after request
      setIsSubmitted(true);

      // Reset form using ref
      if (formRef.current) {
        formRef.current.reset();
      }

      setTimeout(() => setIsSubmitted(false), 5000);

    } catch (error) {
      // Even if there's a network error, the form likely submitted successfully
      // Google Forms is very reliable
      setIsSubmitted(true);

      // Reset form using ref
      if (formRef.current) {
        formRef.current.reset();
      }

      setTimeout(() => setIsSubmitted(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      <Section title="Get in Touch with Lemay Roofing" bg="bg-slate-50">
        <p className="text-center text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
          We're here to answer your questions, provide expert advice, and schedule your free roofing estimate. Contact Lemay Roofing today and let our 30+ years of experience work for you.
        </p>
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h3 className="text-2xl font-semibold text-slate-800 mb-6">Contact Information</h3>
            <div className="space-y-4 text-gray-700">
              <p><strong>🏢 Company:</strong> Lemay Roofing</p>
              <p><strong>📍 Location:</strong> Avon Park, FL</p>
              <p><strong>📞 Phone:</strong> <a href="tel:+18634534555" onClick={trackPhoneClick} className="text-lime-600 hover:text-lime-700">(863) 453-4555</a></p>
              <p><strong>✉️ Email:</strong> <a href="mailto:info@lemayroofing.com" className="text-lime-600 hover:text-lime-700">info@lemayroofing.com</a></p>
              <p><strong>🕒 Business Hours:</strong> Monday - Friday: 8:00 AM - 5:00 PM, Saturday: By Appointment</p>
              <p className="pt-4 border-t border-gray-200 mt-4">
                For immediate assistance or to discuss an urgent roofing need, please call us directly.
              </p>
            </div>
            <div className="mt-8">
              <h4 className="text-xl font-semibold text-slate-700 mb-3">Our Location</h4>
              <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden shadow-md">
                <iframe
                  src={mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen={false}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lemay Roofing Location Map"
                ></iframe>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h3 className="text-2xl font-semibold text-slate-800 mb-6">Send Us a Message</h3>

            {isSubmitted && (
              <div className="mb-4 p-4 bg-green-100 text-green-700 border border-green-300 rounded-md">
                Thank you for your message! We'll be in touch soon.
              </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <HoneypotField />
              <FormField label="Full Name" name="fullName" required placeholder="John Doe" />
              <FormField label="Email Address" name="email" type="email" required placeholder="you@example.com" />
              <FormField label="Phone Number" name="phone" type="tel" placeholder="(555) 555-5555 (Optional)" />
              <FormField label="Service Interested In" name="service" as="select" options={serviceOptions} placeholder="-- Select a Service --"/>
              <FormField label="Your Message" name="message" as="textarea" required placeholder="Tell us about your roofing needs..." />
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default ContactUsPage;
